<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import {
  StepIconsFieldDataQuery,
  StepIconsFieldDataQueryVariables,
  StepIconsFieldSetMutation,
  StepIconsFieldSetMutationVariables,
} from '@/generated/graphql'
import { itemSortByName } from '@/app'
import AutocompleteField from '@/components/input/AutocompleteField.vue'

const props = defineProps<{
  stepId: string
  disabled?: boolean
}>()
const emit = defineEmits<{
  updated: []
}>()

const { t } = useI18n()

const fetchQuery = useQuery<StepIconsFieldDataQuery, StepIconsFieldDataQueryVariables>(
  gql`
    query StepIconsFieldData($stepId: ID!) {
      product {
        icons {
          id
          name
          imageUpload {
            id
            imageUrl(options: { width: 48, height: 48 })
          }
        }
        step(id: $stepId) {
          id
          icons {
            id
          }
          missingIcons {
            id
            name
          }
        }
      }
    }
  `,
  () => ({ stepId: props.stepId }),
)
const step = computed(() => fetchQuery.result.value?.product.step)
const missingIcons = computed(() => step.value?.missingIcons ?? [])
const selectedIds = computed(() => step.value?.icons.map((i) => i.id) ?? [])
const options = computed(() =>
  itemSortByName(fetchQuery.result.value?.product.icons, (i) => i.name).map((i) => ({
    title: i.name,
    value: i.id,
    imageUrl: i.imageUpload?.imageUrl,
  })),
)
type IconOption = (typeof options.value)[0]

const setMutation = useMutation<StepIconsFieldSetMutation, StepIconsFieldSetMutationVariables>(gql`
  mutation StepIconsFieldSet($command: SetStepIcons!) {
    product {
      setStepIcons(command: $command) {
        id
      }
    }
  }
`)

async function save(iconIds: string[]) {
  await setMutation.mutate({ command: { stepId: props.stepId, iconIds } })
  await fetchQuery.refetch()
  emit('updated')
}

function select(ids: string[] | undefined) {
  save([...(ids ?? []), ...missingIcons.value.map((i) => i.id)])
}

function removeMissing(id: string) {
  save([...selectedIds.value, ...missingIcons.value.map((i) => i.id).filter((i) => i !== id)])
}
</script>

<template>
  <autocomplete-field
    :model-value="selectedIds"
    :items="options"
    :disabled="props.disabled"
    :loading="setMutation.loading.value"
    multiple
    chips
    closable-chips
    @update:model-value="select"
  >
    <template #chip="{ props: chipProps, item }">
      <v-chip v-bind="chipProps" :text="item.raw.title">
        <template v-if="(item.raw as IconOption).imageUrl" #prepend>
          <v-avatar :image="(item.raw as IconOption).imageUrl" rounded="0" start />
        </template>
      </v-chip>
    </template>
    <template #item="{ props: itemProps, item }">
      <v-list-item v-bind="itemProps">
        <template v-if="(item.raw as IconOption).imageUrl" #prepend>
          <v-avatar :image="(item.raw as IconOption).imageUrl" rounded="0" />
        </template>
      </v-list-item>
    </template>
  </autocomplete-field>
  <div v-if="missingIcons.length > 0" class="mt-2">
    <v-chip
      v-for="icon in missingIcons"
      :key="icon.id"
      color="warning"
      variant="outlined"
      prepend-icon="warning"
      class="mr-1 mb-1"
      :closable="!props.disabled"
      :title="t('entity.icon.missing')"
      @click:close="removeMissing(icon.id)"
    >
      {{ icon.name }} ({{ t('entity.icon.missing') }})
    </v-chip>
  </div>
</template>
